<script>
/* global axios */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ApiClient from 'dashboard/api/ApiClient';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const mazamWebAPI = new ApiClient('mazam_web', { accountScoped: true });
// Debounce do preview de zona/colisão (Task B2): espera o operador parar de
// digitar antes de bater no /domain_check (proxy do domain-provisioner) —
// senão cada tecla vira uma chamada.
const DOMAIN_CHECK_DEBOUNCE_MS = 600;

export default {
  components: { PageHeader, NextButton },
  data() {
    return {
      channelName: '',
      agentBotId: '',
      domain: '',
      color: '#008069',
      siteTitle: '',
      logo: '',
      // Textos da 1ª tela do webchat — pré-preenchidos como sugestão a partir do nome.
      startTitle: '',
      startSubtitle: '',
      startConsent: '',
      startEdited: false,
      // A1 do review 14/08: quando o modelo PADRÃO de site já traz os textos
      // da 1ª tela, a sugestão automática por nome (applyStartSuggestions)
      // para de rodar — senão, ao digitar o nome da caixa, ela sobrescreve o
      // que veio do modelo antes mesmo do operador ver.
      startFromTemplate: false,
      // Time + acesso: o time do cliente enxerga SÓ esta caixa (InboxMember).
      teamId: '',
      newTeamName: '',
      selectedAgentIds: [],
      creating: false,
      // Preview de zona/colisão (Task B2): null = ainda não checou;
      // { loading: true } enquanto espera o /domain_check; depois o corpo
      // devolvido pelo provisioner (ou { error: true } se falhou/timeout).
      domainCheck: null,
      domainCheckTimer: null,
    };
  },
  watch: {
    // Enquanto o operador não editar os textos à mão NEM eles terem vindo do
    // modelo padrão (A1 do review 14/08), seguem o nome da empresa digitado.
    channelName(v) {
      if (!this.startEdited && !this.startFromTemplate) this.applyStartSuggestions(v);
    },
    cleanDomain(v) {
      this.scheduleDomainCheck(v);
    },
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      teams: 'teams/getTeams',
      agents: 'agents/getAgents',
    }),
    accountId() {
      return this.$route.params.accountId;
    },
    cleanDomain() {
      return this.domain
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/\/.*$/, '')
        .replace(/^www\./, '');
    },
    // Qual estado mostrar no bloco de preview — segue a prioridade do brief:
    // carregando > colisão > zona ausente > DNS externo > ok > erro de rede.
    domainCheckKind() {
      const dc = this.domainCheck;
      if (!dc) return null;
      if (dc.loading) return 'loading';
      if (dc.error) return 'error';
      if (dc.zone_found && dc.already_used_by) return 'collision';
      if (!dc.zone_found) return 'no-zone';
      if (dc.dns_record && dc.dns_record.exists && !dc.dns_record.ours) return 'dns-foreign';
      if (dc.ok_to_provision && dc.zone_found) return 'ok';
      return null;
    },
    // Só o pedaço do domínio que vira registro DNS (ex.: chat.mazam.org na
    // zona mazam.org -> "chat"; domínio igual à zona -> "@", raiz).
    domainCheckSub() {
      const dc = this.domainCheck;
      const zone = dc && dc.zone_name;
      if (!zone) return '';
      const d = this.cleanDomain;
      if (d === zone) return '@';
      return d.endsWith(`.${zone}`) ? d.slice(0, -(zone.length + 1)) : d;
    },
    domainCheckBoxStyle() {
      const styles = {
        loading: 'background:rgba(148,163,184,.10);border:1px solid rgba(148,163,184,.35)',
        ok: 'background:rgba(22,163,74,.08);border:1px solid rgba(22,163,74,.35)',
        collision: 'background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.35)',
        'dns-foreign': 'background:rgba(239,68,68,.08);border:1px solid rgba(239,68,68,.35)',
        'no-zone': 'background:rgba(29,155,240,.08);border:1px solid rgba(29,155,240,.35)',
        error: 'background:rgba(148,163,184,.10);border:1px solid rgba(148,163,184,.35)',
      };
      return styles[this.domainCheckKind] || styles['no-zone'];
    },
  },
  mounted() {
    this.$store.dispatch('agentBots/get');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('agents/get');
    this.carregarModeloPadrao();
  },
  beforeUnmount() {
    clearTimeout(this.domainCheckTimer);
  },
  methods: {
    // Debounce: reseta a espera a cada tecla; só bate no backend quando o
    // operador para de digitar por DOMAIN_CHECK_DEBOUNCE_MS.
    scheduleDomainCheck(domain) {
      clearTimeout(this.domainCheckTimer);
      if (!domain) {
        this.domainCheck = null;
        return;
      }
      this.domainCheck = { loading: true };
      this.domainCheckTimer = setTimeout(() => {
        this.runDomainCheck(domain);
      }, DOMAIN_CHECK_DEBOUNCE_MS);
    },
    async runDomainCheck(domain) {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/mazam_web/domain_check`,
          { params: { domain } }
        );
        // Corrida: se o operador já digitou outra coisa enquanto a resposta
        // vinha, descarta — senão o preview do domínio antigo pisca por cima.
        if (domain !== this.cleanDomain) return;
        this.domainCheck = { loading: false, ...data };
      } catch (error) {
        if (domain !== this.cleanDomain) return;
        this.domainCheck = { loading: false, error: true };
      }
    },
    // A1 do review 14/08: pré-carrega o formulário com o modelo de site
    // PADRÃO (cor, logo, título da aba, textos da 1ª tela) — em vez de o
    // formulário sempre mandar os defaults fixos, que hoje vencem o modelo no
    // merge do #create (o request sempre vence, ver mesclar_do_modelo). Assim
    // o operador já VÊ o que a caixa vai nascer e pode ajustar antes de criar,
    // e o que for enviado passa a refletir essa escolha real.
    // Restrito ao id 1 no servidor (site_template_default_config), coerente
    // com A2 e com o próprio #create — só o id 1 cria caixa SITE WEB de
    // qualquer forma. Sem modelo padrão (ou sem permissão), o catch mantém os
    // defaults de hoje: zero regressão.
    async carregarModeloPadrao() {
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/mazam_web/site_templates/padrao`
        );
        const c = data.config || {};
        if (c.mazam_web_theme && c.mazam_web_theme.brand) {
          this.color = c.mazam_web_theme.brand;
        }
        if (c.mazam_web_brand) {
          if (c.mazam_web_brand.logo) this.logo = c.mazam_web_brand.logo;
          if (c.mazam_web_brand.site_title) this.siteTitle = c.mazam_web_brand.site_title;
        }
        if (c.mazam_web_start) {
          const s = c.mazam_web_start;
          if (s.title || s.subtitle || s.consent) {
            this.startTitle = s.title || '';
            this.startSubtitle = s.subtitle || '';
            this.startConsent = s.consent || '';
            this.startFromTemplate = true;
          }
        }
      } catch (e) {
        // sem modelo padrão ou sem permissão para lê-lo -> mantém os defaults de hoje
      }
    },
    applyStartSuggestions(name) {
      const n = (name || '').trim();
      this.startTitle = n ? `Fale com a ${n}` : '';
      this.startSubtitle = `Informe seu WhatsApp e um especialista${
        n ? ` da ${n}` : ''
      } te atende na hora. Rápido e seguro.`;
      this.startConsent = `Ao continuar, você concorda em ser contatado${
        n ? ` pela ${n}` : ''
      } neste número.`;
    },
    pickLogo(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      if (file.size > 300 * 1024) {
        useAlert('Logo muito grande (máx. 300 KB). Use um PNG menor.');
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        this.logo = reader.result;
      };
      reader.readAsDataURL(file);
    },
    async createChannel() {
      if (!this.channelName.trim()) {
        useAlert('Informe um nome para a caixa.');
        return;
      }
      this.creating = true;
      try {
        const { data } = await mazamWebAPI.create({
          name: this.channelName.trim(),
          agent_bot_id: this.agentBotId || null,
          domain: this.cleanDomain,
          site_name: this.channelName.trim(),
          theme: { brand: this.color },
          brand: {
            logo: this.logo,
            favicon: '',
            site_title: this.siteTitle.trim() || this.channelName.trim(),
          },
          start: {
            title: this.startTitle.trim(),
            subtitle: this.startSubtitle.trim(),
            consent: this.startConsent.trim(),
          },
          // Time + acesso na mesma criação -> o backend cria/associa o Time e
          // adiciona os agentes como InboxMember (isolamento) + TeamMember.
          team_id: this.teamId || null,
          team_name: this.newTeamName.trim() || null,
          agent_ids: this.selectedAgentIds,
        });
        // A caixa pode nascer com pendencia: registro no webchat, avatar ou
        // vinculo de agentes falham SEM derrubar a criacao (200 + `avisos`).
        // Sem mostrar isso aqui, o operador entrega ao cliente uma caixa
        // incompleta achando que esta pronta — foi assim que o dominio ficou
        // com link morto e ninguem soube ate o cliente reclamar.
        (data.avisos || []).forEach(aviso => useAlert(aviso));
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: data.id },
        });
      } catch (error) {
        // O corpo do 422 traz a mensagem acionável (nome de time já existente,
        // time inexistente, caixa não criada). Descartá-lo era o motivo de o
        // operador ver só "não foi possível" e tentar de novo — 4 vezes em 57
        // segundos em 15:47/15:48. Mesmo padrão de MoreActions.vue/DisparosPage.vue.
        useAlert(
          error?.response?.data?.error ||
            'Não foi possível criar a caixa do site.'
        );
      } finally {
        this.creating = false;
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6 overflow-auto">
    <PageHeader
      header-title="SITE WEB"
      header-content="Cria uma caixa de entrada web estilo WhatsApp servida no domínio que você escolher, já com o time do cliente. Cores, logo e textos podem ser ajustados depois na aba Configuração do Site."
    />
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel()">
      <div class="flex-shrink-0 flex-grow-0">
        <label>
          Nome da caixa
          <input v-model="channelName" type="text" placeholder="Ex.: Site RDC Promotora" />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-2">
        <label>
          Domínio do site
          <input v-model="domain" type="text" placeholder="Ex.: rdcpromotora.online" />
        </label>
        <div
          v-if="cleanDomain"
          class="mt-2 p-4 rounded-lg text-sm"
          :style="domainCheckBoxStyle"
        >
          <p v-if="domainCheckKind === 'loading'" class="font-semibold">
            🔎 Checando {{ cleanDomain }}…
          </p>

          <p v-else-if="domainCheckKind === 'collision'" class="font-semibold">
            ⚠️ Este endereço já é usado por outro serviço
            ({{ domainCheck.already_used_by }}). Escolha outro subdomínio.
          </p>

          <p v-else-if="domainCheckKind === 'no-zone'" class="font-semibold">
            ℹ️ Este domínio ainda não está na nossa conta Cloudflare. Adicione a
            zona lá primeiro, ou use um subdomínio de um domínio já nosso
            (ex.: seunome.mazam.org).
          </p>

          <p v-else-if="domainCheckKind === 'dns-foreign'" class="font-semibold">
            ⚠️ Já existe apontamento DNS externo para este endereço — não
            vamos sobrescrever.
          </p>

          <p v-else-if="domainCheckKind === 'ok'" class="font-semibold">
            ✅ Zona detectada: <b>{{ domainCheck.zone_name }}</b> — vamos criar
            só o registro <code>{{ domainCheckSub }}</code> e o endereço
            <b>https://{{ cleanDomain }}</b> fica no ar automático (DNS +
            certificado).
          </p>

          <p v-else class="font-semibold">
            Não consegui checar agora; você ainda pode salvar (a checagem roda
            de novo na publicação).
          </p>
        </div>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-2">
        <label>
          Cor principal do site
          <div style="display:flex;align-items:center;gap:10px">
            <input v-model="color" type="color" style="width:52px;height:36px;padding:2px" />
            <input v-model="color" type="text" style="width:120px" class="!mb-0" />
            <span class="text-xs opacity-70">Verde WhatsApp: #008069 · Azul: #1d9bf0</span>
          </div>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-2">
        <label>
          Título da aba do navegador
          <input v-model="siteTitle" type="text" placeholder="Ex.: RDC Promotora (vazio = nome da caixa)" />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <p class="text-sm font-semibold mb-1">Textos da 1ª tela do webchat</p>
        <p class="help-text" style="margin-top:0">
          Já sugerimos a partir do nome — só ajustar se quiser. Pode editar depois na aba
          Configuração do Site.
        </p>
        <label class="mt-2">
          Título
          <input v-model="startTitle" type="text" @input="startEdited = true" placeholder="Ex.: Fale com a RDC Promotora" />
        </label>
        <label class="mt-2">
          Subtítulo
          <textarea v-model="startSubtitle" rows="2" @input="startEdited = true" placeholder="Ex.: Informe seu WhatsApp e um especialista te atende na hora."></textarea>
        </label>
        <label class="mt-2">
          Aviso de consentimento
          <input v-model="startConsent" type="text" @input="startEdited = true" placeholder="Ex.: Ao continuar, você concorda em ser contatado neste número." />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-2">
        <label>
          Logomarca (PNG/JPG até 300 KB — usada no app instalado e ícones)
          <div style="display:flex;align-items:center;gap:12px">
            <img
              v-if="logo"
              :src="logo"
              alt="logo"
              style="width:44px;height:44px;border-radius:10px;object-fit:cover"
            />
            <input type="file" accept="image/png,image/jpeg" @change="pickLogo" />
          </div>
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-2">
        <label>
          Time do cliente
          <select v-model="teamId">
            <option value="">Criar um time novo…</option>
            <option v-for="team in teams" :key="team.id" :value="team.id">
              {{ team.name }}
            </option>
          </select>
        </label>
        <label v-if="!teamId" class="mt-2">
          Nome do time novo
          <input v-model="newTeamName" type="text" placeholder="Ex.: RDC Promotora" />
        </label>
        <p class="help-text">
          O time agrupa os atendentes deste cliente. Cada atendente adicionado abaixo
          passa a ver <b>somente esta caixa</b>.
        </p>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-2">
        <label>
          Atendentes deste cliente (só verão esta caixa)
          <select v-model="selectedAgentIds" multiple style="min-height:120px">
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }} — {{ agent.email }}
            </option>
          </select>
        </label>
        <p class="help-text">
          Segure Ctrl/Cmd para selecionar vários. Ficam como membros da caixa
          (isolamento) e do time. Pode ajustar depois.
        </p>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-2">
        <label>
          IA (opcional)
          <select v-model="agentBotId">
            <option value="">Sem IA (só atendente humano)</option>
            <option v-for="bot in agentBots" :key="bot.id" :value="bot.id">
              {{ bot.name }}
            </option>
          </select>
        </label>
        <p class="help-text">
          A mesma IA usada nas caixas de WhatsApp. Pode trocar depois nas
          configurações da caixa.
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="creating"
          type="submit"
          solid
          blue
          label="Criar caixa do site"
        />
      </div>
    </form>
  </div>
</template>
